/**
 * Kobotto Logo Component
 *
 * "Orbit" mark — four partners on a continuous ring, a shared core at the
 * centre. Encodes the brand etymology: Collaboration (Kobo) + Auto (Otto).
 * Four-fold rotational symmetry so the mark reads equally at any rotation
 * or scale; all primitives have generous stroke/fill so the glyph stays
 * legible down to 16px.
 */

import React, { useMemo } from 'react';

interface KobottoLogoProps {
  size?: number;
  showText?: boolean;
  text?: string;
  className?: string;
}

const MARK_COLOR = 'rgba(255, 255, 255, 0.95)';

const KobottoLogo: React.FC<KobottoLogoProps> = ({
  size = 48,
  showText = false,
  text = 'Kobotto',
  className = ''
}) => {
  // Memoize text style to prevent re-creating on every render
  const textStyle = useMemo(() => ({
    fontSize: size * 0.4,
    backgroundImage: 'linear-gradient(to right, hsl(var(--primary)), hsl(var(--accent)), hsl(var(--ring)))',
    WebkitBackgroundClip: 'text',
    WebkitTextFillColor: 'transparent'
  }), [size]);

  return (
    <div className={`flex items-center ${showText ? 'gap-2' : ''} ${className}`}>
      <svg
        width={size}
        height={size}
        viewBox="0 0 100 100"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        aria-label="Kobotto logo"
        role="img"
      >
        {/* Orbit ring — the continuous "auto" path binding the partners */}
        <circle
          cx="50"
          cy="50"
          r="30"
          fill="none"
          stroke={MARK_COLOR}
          strokeWidth="4"
        />

        {/* Four partners — collaborators arranged with 4-fold symmetry */}
        <circle cx="50" cy="20" r="7" fill={MARK_COLOR} />
        <circle cx="80" cy="50" r="7" fill={MARK_COLOR} />
        <circle cx="50" cy="80" r="7" fill={MARK_COLOR} />
        <circle cx="20" cy="50" r="7" fill={MARK_COLOR} />

        {/* Shared core — the collaboration nexus */}
        <circle cx="50" cy="50" r="6" fill={MARK_COLOR} />
      </svg>

      {/* Company name with dynamic gradient using theme colors */}
      {showText && (
        <span
          className="font-bold bg-clip-text text-transparent tracking-tight"
          style={textStyle}
        >
          {text}
        </span>
      )}
    </div>
  );
};

export default KobottoLogo;